<template>
  <section class="fomo-dashboard-card retention-editor" :aria-labelledby="`retention-title-${event.id}`">
    <header>
      <span class="workspace-kicker">Liquidación al organizador</span>
      <h4 :id="`retention-title-${event.id}`">Retención del evento</h4>
      <p>Define cuánto queda disponible antes del evento y cuándo se libera el resto. Solo administradores del sistema.</p>
    </header>
    <p v-if="loading" role="status">Cargando retención…</p>
    <form v-else-if="policy" @submit.prevent="save">
      <div class="retention-fields">
        <label class="workspace-field">
          <span>Disponible antes del evento (%)</span>
          <input v-model="percent" :disabled="saving" class="workspace-input" type="number" min="0" max="100" step="0.01" required />
          <small>De 0 a 100. El resto se retiene hasta el cierre y la espera indicada.</small>
        </label>
        <label class="workspace-field">
          <span>Espera después del cierre (horas)</span>
          <input v-model="hours" :disabled="saving" class="workspace-input" type="number" min="0" step="1" required />
          <small>0 libera el resto al cerrar el evento; 24 equivale a un día.</small>
        </label>
      </div>
      <div class="retention-preview" aria-live="polite">
        <strong v-if="valid">{{ percentLabel }}% antes · {{ remainingLabel }}% después</strong>
        <p v-if="valid && Number(percent) === 100">Todo el saldo queda disponible antes del evento, sin retención.</p>
        <p v-else-if="valid && releaseLabel">Liberación del resto: {{ releaseLabel }} ({{ eventTimeZone }}).</p>
        <p v-else-if="valid">La liberación se calculará cuando el evento tenga fecha de cierre.</p>
        <p v-else>Ingresa un porcentaje entre 0 y 100 y un número entero de horas igual o mayor que 0.</p>
      </div>
      <p v-if="policy.mode !== 'split_60_40_event'" class="retention-note">Al guardar se aplicará la distribución indicada arriba.</p>
      <p class="retention-note">La fecha se calcula desde el cierre del evento. El cambio actualiza los saldos disponibles; no modifica transferencias pagadas o en proceso ni envía dinero.</p>
      <div class="retention-actions">
        <FomoButton type="submit" variant="action" icon="fas fa-save" :disabled="saving || !valid || !dirty">
          {{ saving ? 'Guardando…' : 'Guardar retención' }}
        </FomoButton>
        <FomoButton :disabled="saving || !dirty" icon="fas fa-undo" @click="reset">Restablecer</FomoButton>
      </div>
    </form>
    <p v-if="error" class="workspace-feedback workspace-feedback--error" role="alert">{{ error }}</p>
    <FomoButton v-if="!loading && !policy" @click="load">Reintentar</FomoButton>
    <p v-if="success && !dirty" class="workspace-feedback workspace-feedback--success" role="status">{{ success }}</p>
  </section>
</template>

<script>
import { apiService } from '../../common/api.services.js'
import { formatEventDate, resolveEventTimeZone } from '../../common/event-timezone.js'
import FomoButton from '../ui/FomoButton.vue'
import '../../styles/fomo-dashboard.css'

export default {
  name: 'EventPayoutPolicyEditor',
  components: { FomoButton },
  props: { event: { type: Object, required: true } },
  data() {
    return { policy: null, percent: '', hours: '', loading: false, saving: false, error: '', success: '' }
  },
  computed: {
    valid() {
      return String(this.percent).trim() !== '' && String(this.hours).trim() !== '' &&
        Number.isFinite(Number(this.percent)) && Number(this.percent) >= 0 && Number(this.percent) <= 100 &&
        Number.isInteger(Number(this.hours)) && Number(this.hours) >= 0
    },
    dirty() {
      if (!this.policy) return false
      return this.policy.mode !== 'split_60_40_event' ||
        Number(this.percent) !== Number(this.policy.pre_event_cap_percent) ||
        Number(this.hours) !== Number(this.policy.post_event_buffer_hours)
    },
    percentLabel() { return Number(this.percent).toLocaleString('es-EC', { maximumFractionDigits: 2 }) },
    remainingLabel() { return (100 - Number(this.percent)).toLocaleString('es-EC', { maximumFractionDigits: 2 }) },
    eventTimeZone() { return resolveEventTimeZone(this.event) },
    releaseLabel() {
      if (!this.valid || !this.event.end_date) return ''
      const date = new Date(new Date(this.event.end_date).getTime() + Number(this.hours) * 3600000)
      if (!Number.isFinite(date.getTime())) return ''
      return formatEventDate(date, this.event, { dateStyle: 'medium', timeStyle: 'short' })
    },
  },
  created() { this.load() },
  methods: {
    errorMessage(data) {
      if (!data || typeof data !== 'object') return 'No se pudo guardar la retención.'
      const message = data.err || data.detail || Object.values(data)[0]
      return Array.isArray(message) ? message.join(' ') : String(message || 'No se pudo guardar la retención.')
    },
    reset() {
      this.percent = this.policy.mode === 'split_60_40_event' ? this.policy.pre_event_cap_percent : '0.00'
      this.hours = this.policy.post_event_buffer_hours
      this.error = ''
      this.success = ''
    },
    async load() {
      this.loading = true
      this.error = ''
      try {
        const data = await apiService(`/api/admin/events/${this.event.id}/payout-policy/`)
        if (!data || !data.mode || data.pre_event_cap_percent == null || data.post_event_buffer_hours == null) {
          this.error = this.errorMessage(data)
          return
        }
        this.policy = data
        this.reset()
      } catch (_error) {
        this.error = 'No se pudo cargar la retención.'
      } finally { this.loading = false }
    },
    async save() {
      if (this.saving || !this.valid || !this.dirty) return
      this.saving = true
      this.error = ''
      this.success = ''
      try {
        const data = await apiService(`/api/admin/events/${this.event.id}/payout-policy/`, 'PATCH', {
          mode: 'split_60_40_event',
          pre_event_cap_percent: Number(this.percent).toFixed(2),
          post_event_buffer_hours: Number(this.hours),
        })
        if (!data || data.mode !== 'split_60_40_event' || data.pre_event_cap_percent == null || data.post_event_buffer_hours == null) {
          this.error = this.errorMessage(data)
          return
        }
        this.policy = data
        this.reset()
        this.success = 'Retención guardada. Los saldos del evento ya están actualizados.'
      } catch (_error) {
        this.error = 'No se pudo guardar la retención.'
      } finally { this.saving = false }
    },
  },
}
</script>

<style scoped>
.retention-editor { padding: 24px; display: grid; gap: 16px; margin: 20px 0; min-width: 0; }
.retention-editor header { display: grid; gap: 8px; }
.retention-editor h4 { margin: 0; font-family: var(--fomo-font-heading); font-size: 24px; }
.retention-editor p { margin: 0; font-size: 14px; line-height: 1.6; }
.retention-editor form { display: grid; gap: 16px; }
.retention-fields { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
.retention-fields small, .retention-note { opacity: .75; }
.retention-preview { display: grid; gap: 8px; }
.retention-preview strong { font-size: 18px; }
.retention-actions { display: flex; flex-wrap: wrap; gap: 12px; }
@media (max-width: 640px) {
  .retention-editor { padding: 18px; }
  .retention-editor h4 { font-size: 21px; }
  .retention-fields { grid-template-columns: 1fr; }
  .retention-actions { flex-direction: column; align-items: stretch; }
}
</style>
